import compareDeep from 'lodash.isequal';

import {
  District,
  DistrictId,
  DistrictListing,
  LegislativeDistrict
} from '../../services/APIService';

type FilterRangeConfiguration = {
  min: number;
  max: number;
  nodes: {
    range: string;
    value: number;
    min: number;
    max: number;
  }[];
};

type MinMaxValue = { min: number; max: number };
type SidePanel = 'LIST' | 'FILTER' | 'DETAILS';

export interface State {
  sidePanel: SidePanel;
  panelVisible: boolean;
  previousSidePanel?: SidePanel;
  filterConfigurations?: {
    districtType: string[];
    legislativeDistrict: LegislativeDistrict[];
    acreage: FilterRangeConfiguration;
    yearCreated: FilterRangeConfiguration;
    bondsOutstanding: FilterRangeConfiguration;
    assessedValue: FilterRangeConfiguration;
    waterConnections: FilterRangeConfiguration;
    sanitaryConnections: FilterRangeConfiguration;
  };
  filter: {
    districtName?: string;
    awbdMembership?: boolean;
    region?: string[];
    mapExtent?: [number, number, number, number];
    districtType?: string[];
    legislativeDistrict?: string[];
    acreage?: MinMaxValue;
    yearCreated?: MinMaxValue;
    bondsOutstanding?: MinMaxValue;
    assessedValue?: MinMaxValue;
    waterConnections?: MinMaxValue;
    sanitaryConnections?: MinMaxValue;
  };
  isDistrictListingsLoading: boolean;
  /**
   * Districts to be listed in filter results
   * with only data need for each district to display in the list UI.
   */
  districtListings: DistrictListing[];
  /**
   * District listing when initially set. Full district fetched and replaced after.
   * -> Select by ID
   * -> Selected district is listing based on ID
   * -> Full details fetched
   * -> Selected updated to use full details
   */
  selectedDistrict: DistrictListing | District | null;
  /**
   * Map extent changed trigger from state and the time of the change.
   * The time is helpful in reliably managing value changes.
   */
  mapNavigation?: {
    extent: [number, number, number, number];
    timestamp: number;
  };
  error?: { message: string };
  isNoticeOpen: boolean;
}

// #region Actions
export enum ActionType {
  TOGGLE_PANEL_VISIBLE = 'TOGGLE_PANEL_VISIBLE',

  LOAD_FILTER_CONFIGURATIONS = 'LOAD_FILTER_CONFIGURATIONS',

  OPEN_FITLERS_PANEL = 'OPEN_FILTERS_PANEL',
  CLOSE_FILTERS_PANEL = 'CLOSE_FILTERS_PANEL',

  APPLY_MAP_EXTENT_FILTER = 'APPLY_MAP_EXTENT_FILTER',
  CLEAR_MAP_EXTENT_FILTER = 'CLEAR_MAP_EXTENT_FILTER',

  APPLY_FILTERS = 'APPLY_ADVANCED_FILTERS',

  LOAD_DISTRICT_LISTINGS = 'LOAD_DISTRICT_LISTINGS',
  DISTRICT_LISTINGS_SEARCH_START = 'DISTRICT_LISTINGS_SEARCH_START',
  DISTRICT_LISTINGS_SEARCH_COMPLETE = 'DISTRICT_LISTINGS_SEARCH_COMPLETE',

  SELECT_DISTRICT = 'SELECT_DISTRICT',
  LOAD_SELECTED_DISTRICT = 'LOAD_SELECTED_DISTRICT',
  CLEAR_SELECTED_DISTRICT = 'CLEAR_SELECTED_DISTRICT',

  NETWORK_ERROR = 'NETWORK_ERROR',
  CLEAR_ERROR = 'CLEAR_ERROR',

  OPEN_NOTICE_SNACKBAR = 'OPEN_NOTICE_SNACKBAR',
  CLOSE_NOTICE_SNACKBAR = 'CLOSE_NOTICE_SNACKBAR',

  NAVIGATE_MAP = 'NAVIGATE_MAP'
}

export type TogglePanelVisible = {
  type: ActionType.TOGGLE_PANEL_VISIBLE;
};

export type LoadFilterConfigurationsActions = {
  type: ActionType.LOAD_FILTER_CONFIGURATIONS;
  payload: { configurations: NonNullable<State['filterConfigurations']> };
};

export type OpenFiltersPanelAction = {
  type: ActionType.OPEN_FITLERS_PANEL;
};

export type CloseFiltersPanelAction = {
  type: ActionType.CLOSE_FILTERS_PANEL;
};

export type ApplyMapExtentFilterAction = {
  type: ActionType.APPLY_MAP_EXTENT_FILTER;
  payload: { extent: NonNullable<State['filter']['mapExtent']> };
};

export type ClearMapExtentFilterAction = {
  type: ActionType.CLEAR_MAP_EXTENT_FILTER;
};

export type ApplyFiltersAction = {
  type: ActionType.APPLY_FILTERS;
  payload: Pick<
    State['filter'],
    | 'districtName'
    | 'awbdMembership'
    | 'region'
    | 'districtType'
    | 'legislativeDistrict'
    | 'acreage'
    | 'yearCreated'
    | 'bondsOutstanding'
    | 'assessedValue'
    | 'waterConnections'
    | 'sanitaryConnections'
  >;
};

/**
 * Replace district listings with response for a new API request.
 * Likely as result of filter change and API query.
 */
export type LoadDistrictListingsAction = {
  type: ActionType.LOAD_DISTRICT_LISTINGS;
  payload: { districtListings: DistrictListing[] };
};

export type DistrictListingsSearchStartAction = {
  type: ActionType.DISTRICT_LISTINGS_SEARCH_START;
};

export type DistrictListingsSearchCompleteAction = {
  type: ActionType.DISTRICT_LISTINGS_SEARCH_COMPLETE;
};

/**
 * Select district by id (`state.selectedDistrict`).
 * District must be in the current filtered listings to be selectable.
 */
export type SelectDistrictAction = {
  type: ActionType.SELECT_DISTRICT;
  payload: { districtId: DistrictId };
};

/**
 * Apply full details for a selected district obtained from an asynchronous request.
 * Details will not be applied if the district is no longer selected.
 */
export type LoadSelectedDistrictAction = {
  type: ActionType.LOAD_SELECTED_DISTRICT;
  payload: { districtDetails: District };
};

export type NetworkErrorAction = {
  type: ActionType.NETWORK_ERROR;
  payload: { message: string };
};

export type ClearErrorAction = {
  type: ActionType.CLEAR_ERROR;
};

export type OpenNoticeSnackbarAction = {
  type: ActionType.OPEN_NOTICE_SNACKBAR;
};

export type CloseNoticeSnackbarAction = {
  type: ActionType.CLOSE_NOTICE_SNACKBAR;
};

export type ClearSelectedDistrictAction = {
  type: ActionType.CLEAR_SELECTED_DISTRICT;
};

export type NavigateMapAction = {
  type: ActionType.NAVIGATE_MAP;
  payload: { extent: [number, number, number, number] };
};

export type Action =
  | TogglePanelVisible
  | LoadFilterConfigurationsActions
  | OpenFiltersPanelAction
  | CloseFiltersPanelAction
  | ApplyMapExtentFilterAction
  | ClearMapExtentFilterAction
  | ApplyFiltersAction
  | LoadDistrictListingsAction
  | DistrictListingsSearchStartAction
  | DistrictListingsSearchCompleteAction
  | SelectDistrictAction
  | LoadSelectedDistrictAction
  | ClearSelectedDistrictAction
  | NetworkErrorAction
  | ClearErrorAction
  | OpenNoticeSnackbarAction
  | CloseNoticeSnackbarAction
  | NavigateMapAction;
// #endregion

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.TOGGLE_PANEL_VISIBLE: {
      return {
        ...state,
        panelVisible: !state.panelVisible
      };
    }
    case ActionType.LOAD_FILTER_CONFIGURATIONS: {
      const { configurations } = action.payload;
      return {
        ...state,
        filterConfigurations: {
          ...configurations,
          // Move other to the end of the list
          districtType: [
            ...configurations.districtType.filter((type) => type !== 'Other'),
            'Other'
          ]
        }
      };
    }
    case ActionType.OPEN_FITLERS_PANEL: {
      return {
        ...state,
        sidePanel: 'FILTER',
        previousSidePanel: state.sidePanel
      };
    }
    case ActionType.CLOSE_FILTERS_PANEL: {
      return {
        ...state,
        sidePanel: 'LIST',
        previousSidePanel: state.sidePanel
      };
    }
    case ActionType.APPLY_MAP_EXTENT_FILTER: {
      const { extent } = action.payload;
      return { ...state, filter: { ...state.filter, mapExtent: extent } };
    }
    case ActionType.CLEAR_MAP_EXTENT_FILTER: {
      return { ...state, filter: { ...state.filter, mapExtent: undefined } };
    }
    case ActionType.APPLY_FILTERS: {
      const { payload } = action;

      const isNoChange = compareDeep(
        {
          districtName: state.filter.districtName,
          awbdMembership: state.filter.awbdMembership,
          region: state.filter.region,
          districtType: state.filter.districtType,
          legislativeDistrict: state.filter.legislativeDistrict,
          acreage: state.filter.acreage,
          yearCreated: state.filter.yearCreated,
          bondsOutstanding: state.filter.bondsOutstanding,
          assessedValue: state.filter.assessedValue,
          waterConnections: state.filter.waterConnections,
          sanitaryConnections: state.filter.sanitaryConnections
        },
        {
          districtName: payload.districtName,
          awbdMembership: payload.awbdMembership,
          region: payload.region,
          districtType: payload.districtType,
          legislativeDistrict: payload.legislativeDistrict,
          acreage: payload.acreage,
          yearCreated: payload.yearCreated,
          bondsOutstanding: payload.bondsOutstanding,
          assessedValue: payload.assessedValue,
          waterConnections: payload.waterConnections,
          sanitaryConnections: payload.sanitaryConnections
        }
      );

      // Only apply filters to state if value has changed
      if (isNoChange) return state;

      return { ...state, filter: { ...state.filter, ...payload } };
    }
    case ActionType.LOAD_DISTRICT_LISTINGS: {
      const { districtListings } = action.payload;
      return { ...state, districtListings };
    }
    case ActionType.DISTRICT_LISTINGS_SEARCH_START: {
      return { ...state, isDistrictListingsLoading: true };
    }
    case ActionType.DISTRICT_LISTINGS_SEARCH_COMPLETE: {
      return { ...state, isDistrictListingsLoading: false };
    }
    case ActionType.SELECT_DISTRICT: {
      const { districtId } = action.payload;

      const district = state.districtListings.find(
        (d) => d.districtId === districtId
      );

      if (!district) return state;

      return {
        ...state,
        sidePanel: 'DETAILS',
        panelVisible: true,
        previousSidePanel:
          state.sidePanel !== 'DETAILS' // Don't allow DETAILS as previous and current
            ? state.sidePanel
            : state.previousSidePanel,
        selectedDistrict: district
      };
    }
    case ActionType.LOAD_SELECTED_DISTRICT: {
      const { districtDetails } = action.payload;

      if (districtDetails.districtId !== state.selectedDistrict?.districtId) {
        // Only apply full details if district is currently selected
        return state;
      }

      return {
        ...state,
        selectedDistrict: districtDetails,
        error: undefined
      };
    }
    case ActionType.CLEAR_SELECTED_DISTRICT: {
      return {
        ...state,
        sidePanel:
          state.previousSidePanel && state.previousSidePanel !== 'DETAILS'
            ? state.previousSidePanel
            : 'FILTER',
        previousSidePanel: state.sidePanel,
        selectedDistrict: null
      };
    }
    case ActionType.NETWORK_ERROR: {
      const { message } = action.payload;

      return { ...state, error: { message } };
    }
    case ActionType.CLEAR_ERROR: {
      return { ...state, error: undefined };
    }
    case ActionType.OPEN_NOTICE_SNACKBAR: {
      return { ...state, isNoticeOpen: true };
    }
    case ActionType.CLOSE_NOTICE_SNACKBAR: {
      return { ...state, isNoticeOpen: false };
    }
    case ActionType.NAVIGATE_MAP: {
      const { extent } = action.payload;
      return {
        ...state,
        mapNavigation: { extent, timestamp: new Date().valueOf() }
      };
    }
    default:
      return state;
  }
};
